import type { RawProfilePoint } from '@/types/profile';

/**
 * Parses a .TUR turning program into the same position/radius point format
 * used throughout the app. Unlike .C (a pre-sampled point table), .TUR is
 * actual G-code — linear moves (G1) and circular arc moves (G2/G3) — which
 * is why it can contain geometry (a knob bulb, a tapered barrel end) that
 * never made it into a coarser .C export: it's the machine's real toolpath,
 * not a downstream sampling of it.
 *
 * Axis convention observed in Mark Lumber's files: `P` is position along
 * the lathe axis, `W` is radius. `G2`/`G3` arcs give the center as an I/J
 * offset from the arc's start point (standard G-code IJK form) — G2 is
 * clockwise, G3 counter-clockwise, in the P/W plane.
 *
 * G0 rapid moves are recorded too (not skipped) so the existing lead-in/out
 * trim logic — already tuned to recognize clearance-radius rapid moves —
 * handles them exactly the same way it does for a .C file, with no special
 * casing needed downstream.
 */

const ARC_SAMPLES_PER_FULL_CIRCLE = 48; // resolution for flattening arcs into points

function parseNum(token: string | undefined): number | undefined {
  if (token === undefined) return undefined;
  const v = parseFloat(token);
  return Number.isFinite(v) ? v : undefined;
}

function extractField(line: string, letter: string): number | undefined {
  const match = line.match(new RegExp(`${letter}(-?[0-9]*\\.?[0-9]+)`));
  return match ? parseNum(match[1]) : undefined;
}

export function parseTUR(text: string): RawProfilePoint[] {
  const lines = text.replace(/\r\n?/g, '\n').split('\n');
  const points: RawProfilePoint[] = [];

  let curP = 0;
  let curW = 0;
  let started = false;

  const push = (p: number, w: number) => {
    points.push({ index: points.length, position: p, radius: w });
  };

  for (const rawLine of lines) {
    // Strip comments (';' to end of line) and surrounding whitespace.
    const line = rawLine.split(';')[0].trim();
    if (!line) continue;

    const gMatch = line.match(/^G([0-3])\b/);
    if (!gMatch) continue; // M-codes, blank/other lines — not a move

    const gCode = gMatch[1];
    const targetP = extractField(line, 'P') ?? curP;
    const targetW = extractField(line, 'W') ?? curW;

    if (!started) {
      curP = targetP;
      curW = targetW;
      push(curP, curW);
      started = true;
      continue;
    }

    if (gCode === '0' || gCode === '1') {
      curP = targetP;
      curW = targetW;
      push(curP, curW);
      continue;
    }

    // G2 (clockwise) / G3 (counter-clockwise) arc, center given as an I/J
    // offset from the START point of the move.
    const i = extractField(line, 'I');
    const j = extractField(line, 'J');

    if (i === undefined || j === undefined) {
      // Malformed arc line — degrade to a straight move rather than
      // dropping the point.
      curP = targetP;
      curW = targetW;
      push(curP, curW);
      continue;
    }

    const startP = curP;
    const startW = curW;
    const centerP = startP + i;
    const centerW = startW + j;
    const radius = Math.hypot(startP - centerP, startW - centerW);

    const startAngle = Math.atan2(startW - centerW, startP - centerP);
    const endAngle = Math.atan2(targetW - centerW, targetP - centerP);

    const clockwise = gCode === '2';
    let delta = endAngle - startAngle;
    if (clockwise && delta > 0) delta -= 2 * Math.PI;
    if (!clockwise && delta < 0) delta += 2 * Math.PI;

    const steps = Math.max(1, Math.round((Math.abs(delta) / (2 * Math.PI)) * ARC_SAMPLES_PER_FULL_CIRCLE));
    for (let s = 1; s <= steps; s++) {
      const t = s / steps;
      const angle = startAngle + delta * t;
      push(centerP + radius * Math.cos(angle), centerW + radius * Math.sin(angle));
    }

    curP = targetP;
    curW = targetW;
  }

  return points;
}
